import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';
import type { IHomeLocation } from '@/domain/entities/home-location';

import {
  fetchLocationsByPropertyId,
  fetchLocationById,
  createLocation,
  updateLocation,
  deleteLocation,
} from './locationsThunks';
import type { LocationsState } from './locationsSliceTypes';
import { DefaultSortSpec, type SortSpec } from '@/domain/shared/sortOptions';

const initialState: LocationsState = {
  itemsByPropertyID: {},
  dirtyByPropertyID: {},
  hasFetchedByPropertyID: {},
  isListingByPropertyID: {},
  errorByPropertyID: {},

  selected: null,
  isLoadingOne: false,
  isSaving: false,
  isDeleting: false,
  hasChanges: false,
  activeSort: DefaultSortSpec,
};

const ensureBucket = (state: LocationsState, propertyID: string) => {
  if (!state.itemsByPropertyID[propertyID]) state.itemsByPropertyID[propertyID] = [];
  if (state.hasFetchedByPropertyID[propertyID] === undefined) state.hasFetchedByPropertyID[propertyID] = false;
  if (state.isListingByPropertyID[propertyID] === undefined) state.isListingByPropertyID[propertyID] = false;
  if (state.errorByPropertyID[propertyID] === undefined) state.errorByPropertyID[propertyID] = null;
  if (!state.dirtyByPropertyID[propertyID]) state.dirtyByPropertyID[propertyID] = {};
};

const upsertById = (items: IHomeLocation[], rec: IHomeLocation): IHomeLocation[] => {
  const id = rec.id;
  if (!id) return [rec, ...items]; // fallback (shouldn’t happen, but safe)
  const idx = items.findIndex((x) => x.id === id);
  if (idx === -1) return [rec, ...items];
  const copy = items.slice();
  copy[idx] = rec;
  return copy;
};

const removeById = (items: IHomeLocation[], id: string): IHomeLocation[] => items.filter((x) => x.id !== id);

export const locationsSlice = createSlice({
  name: 'propertyLocations',
  initialState,
  reducers: {
    setLocationsActiveSort: (state, action: PayloadAction<SortSpec>) => {
      state.activeSort = action.payload;
    },

    clearSelectedLocation: (state) => {
      state.selected = null;
      state.hasChanges = false;
    },

    // ✅ NEW: local patch for any location in a property list (used by the builder typing)
    upsertLocationLocal: (state, action: PayloadAction<{ propertyID: string; item: IHomeLocation }>) => {
      const { propertyID, item } = action.payload;
      ensureBucket(state, propertyID);

      state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], item);

      if (item.id) {
        state.dirtyByPropertyID[propertyID][item.id] = true;
      }
    },

    // ✅ NEW: mark one location clean (after successful save)
    markLocationClean: (state, action: PayloadAction<{ propertyID: string; id: string }>) => {
      const { propertyID, id } = action.payload;
      ensureBucket(state, propertyID);
      state.dirtyByPropertyID[propertyID][id] = false;
    },

    // ✅ NEW: clear all dirty for a property (after refetch/hydration or “save all” if you ever add it)
    clearDirtyForProperty: (state, action: PayloadAction<{ propertyID: string }>) => {
      const { propertyID } = action.payload;
      ensureBucket(state, propertyID);
      state.dirtyByPropertyID[propertyID] = {};
    },
    markLocationDirty: (state, action: PayloadAction<{ propertyID: string; locationID: string }>) => {
      const { propertyID, locationID } = action.payload;

      if (!state.dirtyByPropertyID[propertyID]) {
        state.dirtyByPropertyID[propertyID] = {};
      }

      state.dirtyByPropertyID[propertyID][locationID] = true;
    },

    // Local patch (UI typing) for selected location
    patchSelectedLocation: (state, action: PayloadAction<Partial<IHomeLocation>>) => {
      if (!state.selected) return;

      const updated: IHomeLocation = {
        ...state.selected,
        ...action.payload,
      };

      state.selected = updated;
      state.hasChanges = true;

      const propertyID = updated.propertyID;
      if (propertyID) {
        ensureBucket(state, propertyID);
        state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], updated);
      }
    },

    markLocationsClean: (state) => {
      state.hasChanges = false;
    },

    // Optional helper: write items directly (useful if you hydrate from property builder draft)
    setLocationsForProperty: (state, action: PayloadAction<{ propertyID: string; items: IHomeLocation[] }>) => {
      const { propertyID, items } = action.payload;
      ensureBucket(state, propertyID);
      state.itemsByPropertyID[propertyID] = items;
      state.hasFetchedByPropertyID[propertyID] = true;
      state.errorByPropertyID[propertyID] = null;
      // state.dirtyByPropertyID[propertyID] = {};
    },
  },

  extraReducers: (builder) => {
    builder
      // ---------------- list by property ----------------
      .addCase(fetchLocationsByPropertyId.pending, (state, action) => {
        const propertyID = action.meta.arg.propertyID;
        ensureBucket(state, propertyID);
        state.isListingByPropertyID[propertyID] = true;
        state.errorByPropertyID[propertyID] = null;
      })
      .addCase(fetchLocationsByPropertyId.fulfilled, (state, action) => {
        const propertyID = action.meta.arg.propertyID;
        ensureBucket(state, propertyID);

        state.isListingByPropertyID[propertyID] = false;
        state.itemsByPropertyID[propertyID] = action.payload;
        state.hasFetchedByPropertyID[propertyID] = true;
      })
      .addCase(fetchLocationsByPropertyId.rejected, (state, action) => {
        const propertyID = action.meta.arg.propertyID;
        ensureBucket(state, propertyID);

        state.isListingByPropertyID[propertyID] = false;
        state.hasFetchedByPropertyID[propertyID] = true;
        state.errorByPropertyID[propertyID] =
          (action.payload as { message?: string } | undefined)?.message ?? action.error.message ?? 'Failed to fetch';
      })

      // ---------------- get one ----------------
      .addCase(fetchLocationById.pending, (state) => {
        state.isLoadingOne = true;
      })
      .addCase(fetchLocationById.fulfilled, (state, action) => {
        state.isLoadingOne = false;

        const rec = action.payload;
        state.selected = rec;
        state.hasChanges = false;

        const propertyID = rec.propertyID;
        if (propertyID) {
          ensureBucket(state, propertyID);
          state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
        }
      })
      .addCase(fetchLocationById.rejected, (state) => {
        state.isLoadingOne = false;
      })

      // ---------------- create ----------------
      .addCase(createLocation.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(createLocation.fulfilled, (state, action) => {
        state.isSaving = false;

        const rec = action.payload.data as IHomeLocation;
        state.selected = rec;
        state.hasChanges = false;

        const propertyID = rec.propertyID;
        if (!propertyID) return;

        ensureBucket(state, propertyID);

        const tempId = action.meta.arg.tempId;

        if (tempId) {
          const idx = state.itemsByPropertyID[propertyID].findIndex((x) => x.id === tempId);

          if (idx !== -1) {
            // ✅ replace the temp item in-place (no duplication)
            state.itemsByPropertyID[propertyID][idx] = rec;

            // ✅ cleanup temp tracking
            delete state.dirtyByPropertyID[propertyID][tempId];
          } else {
            // fallback (shouldn't happen often)
            state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
          }
        } else {
          state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
        }

        state.hasFetchedByPropertyID[propertyID] = true;

        // ✅ mark real id as clean
        if (rec.id) state.dirtyByPropertyID[propertyID][rec.id] = false;
      })
      .addCase(createLocation.rejected, (state) => {
        state.isSaving = false;
      })

      // ---------------- update ----------------
      .addCase(updateLocation.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(updateLocation.fulfilled, (state, action) => {
        state.isSaving = false;

        const rec = action.payload;
        state.selected = rec;
        state.hasChanges = false;

        const propertyID = rec.propertyID;
        if (propertyID) {
          ensureBucket(state, propertyID);
          state.itemsByPropertyID[propertyID] = upsertById(state.itemsByPropertyID[propertyID], rec);
          if (rec.id) state.dirtyByPropertyID[propertyID][rec.id] = false;
        }
      })
      .addCase(updateLocation.rejected, (state) => {
        state.isSaving = false;
      })

      // ---------------- delete ----------------
      .addCase(deleteLocation.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteLocation.fulfilled, (state, action) => {
        state.isDeleting = false;

        const { id, propertyID } = action.meta.arg;
        if (propertyID) {
          ensureBucket(state, propertyID);
          state.itemsByPropertyID[propertyID] = removeById(state.itemsByPropertyID[propertyID], id);
          delete state.dirtyByPropertyID[propertyID][id];
        }

        if (state.selected?.id === id) {
          state.selected = null;
          state.hasChanges = false;
        }
      })
      .addCase(deleteLocation.rejected, (state) => {
        state.isDeleting = false;
      });
  },
});

export const {
  clearSelectedLocation,
  patchSelectedLocation,
  markLocationsClean,
  markLocationDirty,
  setLocationsForProperty,
  upsertLocationLocal,
  markLocationClean,
  clearDirtyForProperty,
  setLocationsActiveSort,
} = locationsSlice.actions;

// ---------------- selectors ----------------
const selectLocationsState = (state: RootState) => state.propertyLocations;

export const selectLocationsByPropertyId = createSelector(
  [selectLocationsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return [];
    return s.itemsByPropertyID[propertyID] ?? [];
  },
);

export const selectLocationsListingByPropertyId = createSelector(
  [selectLocationsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return false;
    return s.isListingByPropertyID[propertyID] ?? false;
  },
);

export const selectLocationsFetchedByPropertyId = createSelector(
  [selectLocationsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return false;
    return s.hasFetchedByPropertyID[propertyID] ?? false;
  },
);

export const selectLocationIsDirty = createSelector(
  [selectLocationsState, (_: RootState, propertyID?: string | null, id?: string | null) => ({ propertyID, id })],
  (s, { propertyID, id }) => {
    if (!propertyID || !id) return false;
    return s.dirtyByPropertyID[propertyID]?.[id] ?? false;
  },
);

export const selectDirtyLocationIds = createSelector(
  [selectLocationsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return [];
    const map = s.dirtyByPropertyID[propertyID] ?? {};
    return Object.keys(map).filter((id) => map[id]);
  },
);

export const selectDirtyByPropertyId = createSelector(
  [selectLocationsState, (_: RootState, propertyID?: string | null) => propertyID],
  (s, propertyID) => {
    if (!propertyID) return {};
    return s.dirtyByPropertyID[propertyID] ?? {};
  },
);

export const selectLocationsActiveSort = createSelector([selectLocationsState], (s) => s.activeSort);

export default locationsSlice.reducer;
